import React, { useState, useEffect } from "react";
import {
    Paper,
    Grid,
    TextField,
    IconButton,
    Switch,
    FormControlLabel,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from "@mui/material";
import { makeStyles } from 'tss-react/mui';
import { Formik, Form, Field } from 'formik';
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";

import { Edit as EditIcon, WorkspacePremiumOutlined } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";

import { toast } from "../../services/toast";
import usePlans from "../../hooks/usePlans";
import { i18n } from "../../translate/i18n";
import SettingsCard from "../Settings/SettingsCard";
import FormSectionLabel from "../FormSectionLabel";
import { StandardTableContainer, useStandardTableStyles } from "../StandardTable";


const useStyles = makeStyles()(theme => ({
    root: {
        width: '100%'
    },
    mainPaper: {
        width: '100%',
        flex: 1,
        padding: theme.spacing(1),
        background: "transparent",
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1.5),
    },
    fullWidth: {
        width: '100%'
    },
    actions: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(1),
        justifyContent: "flex-end",
        marginTop: theme.spacing(2),
    },
    featureChips: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(0.5),
        maxWidth: 320,
    },
}));

const DEFAULT_PLAN = {
    name: '',
    users: 0,
    connections: 0,
    queues: 0,
    value: 0,
    useCampaigns: true,
    useSchedules: true,
    useInternalChat: true,
    useExternalApi: true,
    useKanban: true,
    useOpenAi: true,
    useIntegrations: true,
    useGoogleCalendar: true,
    useAvatar: true,
};

const FEATURE_TOGGLES = [
    { name: "useCampaigns", label: "Campañas" },
    { name: "useSchedules", label: "Agendamientos" },
    { name: "useInternalChat", label: "Chat Interno" },
    { name: "useExternalApi", label: "API Externa" },
    { name: "useKanban", label: "Kanban" },
    { name: "useOpenAi", label: "Open.Ai" },
    { name: "useIntegrations", label: "Integraciones" },
    { name: "useGoogleCalendar", label: "Google Calendar" },
    { name: "useAvatar", label: "Avatar de voz" },
];

export function PlanManagerForm(props) {
    const { onSubmit, onDelete, onCancel, initialValue, loading } = props;
    const { classes } = useStyles()

    const [record, setRecord] = useState({ ...DEFAULT_PLAN, ...initialValue });

    useEffect(() => {
        setRecord({ ...DEFAULT_PLAN, ...initialValue })
    }, [initialValue])

    const handleSubmit = async (data) => {
        onSubmit(data)
    }

    return (
        <Formik
            enableReinitialize
            className={classes.fullWidth}
            initialValues={record}
            onSubmit={(values, { resetForm }) =>
                setTimeout(() => {
                    handleSubmit(values)
                    resetForm()
                }, 500)
            }
        >
            {({ values, setFieldValue }) => (
                <Form className={classes.fullWidth}>
                    <Grid spacing={1.5} container>
                        <FormSectionLabel>Datos del plan</FormSectionLabel>
                        <Grid size={12}>
                            <Field
                                as={TextField}
                                label={i18n.t("plans.form.name")}
                                name="name"
                                variant="outlined"
                                size="small"
                                className={classes.fullWidth}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                            <Field
                                as={TextField}
                                label={i18n.t("plans.form.users")}
                                name="users"
                                variant="outlined"
                                size="small"
                                className={classes.fullWidth}
                                type="number"
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                            <Field
                                as={TextField}
                                label={i18n.t("plans.form.connections")}
                                name="connections"
                                variant="outlined"
                                size="small"
                                className={classes.fullWidth}
                                type="number"
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                            <Field
                                as={TextField}
                                label="Areas"
                                name="queues"
                                variant="outlined"
                                size="small"
                                className={classes.fullWidth}
                                type="number"
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                            <Field
                                as={TextField}
                                label="Valor"
                                name="value"
                                variant="outlined"
                                size="small"
                                className={classes.fullWidth}
                                type="text"
                            />
                        </Grid>

                        <FormSectionLabel>Funciones incluidas</FormSectionLabel>
                        {FEATURE_TOGGLES.map(({ name, label }) => (
                            <Grid key={name} size={{ xs: 6, sm: 4 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={Boolean(values[name])}
                                            onChange={(e) => setFieldValue(name, e.target.checked)}
                                        />
                                    }
                                    label={label}
                                />
                            </Grid>
                        ))}
                    </Grid>
                    <div className={classes.actions}>
                        <ButtonWithSpinner loading={loading} onClick={() => onCancel()} variant="outlined">
                            Cancelar
                        </ButtonWithSpinner>
                        {record.id !== undefined && (
                            <ButtonWithSpinner loading={loading} onClick={() => onDelete(record)} variant="outlined" color="secondary">
                                {i18n.t("plans.form.delete")}
                            </ButtonWithSpinner>
                        )}
                        <ButtonWithSpinner loading={loading} type="submit" variant="contained" color="primary">
                            {i18n.t("plans.form.save")}
                        </ButtonWithSpinner>
                    </div>
                </Form>
            )}
        </Formik>
    );
}

export function PlansManagerGrid(props) {
    const { records, onSelect } = props
    const { classes } = useStyles()
    const tableClasses = useStandardTableStyles()

    return (
        <StandardTableContainer>
            <Table size="small" stickyHeader>
                <TableHead className={tableClasses.head}>
                    <TableRow>
                        <TableCell>{i18n.t("plans.form.name")}</TableCell>
                        <TableCell align="center">Valor</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.users")}</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.connections")}</TableCell>
                        <TableCell align="center">Areas</TableCell>
                        <TableCell>Funciones incluidas</TableCell>
                        <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {records.map((row) => (
                        <TableRow key={row.id} hover>
                            <TableCell>{row.name || '-'}</TableCell>
                            <TableCell align="center">
                                {i18n.t("plans.form.money")} {row.value ? Number(row.value).toLocaleString('pt-br', { minimumFractionDigits: 2 }) : '00.00'}
                            </TableCell>
                            <TableCell align="center">{row.users || 0}</TableCell>
                            <TableCell align="center">{row.connections || 0}</TableCell>
                            <TableCell align="center">{row.queues || 0}</TableCell>
                            <TableCell>
                                <div className={classes.featureChips}>
                                    {FEATURE_TOGGLES.filter(({ name }) => row[name] !== false).map(({ name, label }) => (
                                        <Chip key={name} size="small" label={label} variant="outlined" />
                                    ))}
                                </div>
                            </TableCell>
                            <TableCell align="center">
                                <IconButton onClick={() => onSelect(row)} aria-label="editar" size="small">
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            {!records.length && (
                <div className={tableClasses.empty}>Todavía no hay ningún plan cargado.</div>
            )}
        </StandardTableContainer>
    );
}

export default function PlansManager() {
    const { classes } = useStyles()
    const { list, save, update, remove } = usePlans()

    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [modalOpen, setModalOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [records, setRecords] = useState([])
    const [record, setRecord] = useState(DEFAULT_PLAN)

    useEffect(() => {
        async function fetchData() {
            await loadPlans()
        }
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const loadPlans = async () => {
        setLoading(true)
        try {
            const planList = await list()
            setRecords(planList)
        } catch (e) {
            toast.error('No se puede cargar la lista de registros')
        }
        setLoading(false)
    }

    const handleSubmit = async (data) => {
        setLoading(true)

        try {
            if (data.id !== undefined) {
                await update(data)
            } else {
                await save(data)
            }
            await loadPlans()
            handleCancel()
            setModalOpen(false)
            toast.success('Operación realizada con éxito!')
        } catch (e) {
            toast.error('La operación no se pudo realizar.. Verifique se já existe uma plano com o mesmo nome ou se os campos foram preenchidos corretamente')
        }
        setLoading(false)
    }

    const handleDelete = async () => {
        setLoading(true)
        try {
            await remove(record.id)
            await loadPlans()
            handleCancel()
            setModalOpen(false)
            toast.success('Operación realizada con éxito!')
        } catch (e) {
            toast.error('La operación no se pudo realizar.')
        }
        setLoading(false)
    }

    const handleOpenDeleteDialog = () => {
        setShowConfirmDialog(true)
    }

    const handleCancel = () => {
        setRecord({ ...DEFAULT_PLAN, id: undefined })
    }

    const openNewModal = () => {
        handleCancel()
        setModalOpen(true)
    }

    const closeModal = () => {
        setModalOpen(false)
        handleCancel()
    }

    const handleSelect = (data) => {
        const record = { ...DEFAULT_PLAN, id: data.id, name: data.name || '' };
        record.users = data.users || 0;
        record.connections = data.connections || 0;
        record.queues = data.queues || 0;
        record.value = data.value?.toLocaleString('pt-br', { minimumFractionDigits: 0 }) || 0;
        FEATURE_TOGGLES.forEach(({ name }) => {
            record[name] = data[name] === false ? false : true;
        });

        setRecord(record)
        setModalOpen(true)
    }

    return (
        <Paper className={classes.mainPaper} elevation={0}>
            <SettingsCard
                icon={WorkspacePremiumOutlined}
                title={i18n.t("settings.tabs.plans")}
                subtitle="Los planes disponibles para asignarle a una empresa: cuántos usuarios, conexiones y áreas incluye, y qué módulos habilita."
                action={
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={openNewModal}
                    >
                        Nuevo plan
                    </Button>
                }
            />

            <Dialog open={modalOpen} onClose={closeModal} maxWidth="md" fullWidth>
                <DialogTitle>{record.id ? "Editar plan" : "Nuevo plan"}</DialogTitle>
                <DialogContent dividers>
                    <PlanManagerForm
                        initialValue={record}
                        onDelete={handleOpenDeleteDialog}
                        onSubmit={handleSubmit}
                        onCancel={closeModal}
                        loading={loading}
                    />
                </DialogContent>
            </Dialog>

            <PlansManagerGrid
                records={records}
                onSelect={handleSelect}
            />
            <ConfirmationModal
                title="Eliminación de registro"
                open={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
                onConfirm={() => handleDelete()}
            >
                ¿Realmente deseas eliminar este registro?
            </ConfirmationModal>
        </Paper>
    );
}
